
import React, { useState } from 'react';
import { format, addMonths, subMonths } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useChartData } from '@/hooks/use-chart-data';
import { CalendarLegend } from '@/components/calendar/CalendarLegend';
import { HarvestMonthCalendar } from '@/components/calendar/HarvestMonthCalendar';
import HarvestProductTimeline from '@/components/calendar/HarvestProductTimeline';
import { getProductColor } from '@/utils/colorUtils';
import { useQuery } from '@tanstack/react-query';
import { getProducts } from '@/services/productService';
import { UpcomingHarvestsList } from '@/components/calendar/UpcomingHarvestsList';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const HarvestCalendarTimeline: React.FC = () => {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const { calendarEvents, isLoading: eventsLoading, error: eventsError } = useChartData();
  
  // Fetch products from the database
  const { data: products = [], isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['products'],
    queryFn: getProducts
  });
  
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  // Filter events based on selected product
  const filteredEvents = selectedProduct === 'all' 
    ? calendarEvents 
    : calendarEvents.filter(event => event.product === selectedProduct);

  // Log information for debugging
  console.log("Calendar Events:", calendarEvents);
  console.log("Products:", products);
  console.log("Selected Product:", selectedProduct);
  console.log("Filtered Events:", filteredEvents);

  if (eventsLoading || productsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[350px] w-full" />
      </div>
    );
  }

  if (eventsError || productsError) {
    return (
      <div className="p-8 text-center">
        <h3 className="text-lg font-medium text-red-600 mb-2">Error loading calendar data</h3>
        <p className="text-muted-foreground">Please try refreshing the page</p>
      </div>
    );
  }

  // If we have no events, show an empty state
  if (!calendarEvents || calendarEvents.length === 0) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-medium">Calendar View</CardTitle>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="icon" onClick={prevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="font-medium">
                {format(currentMonth, 'MMMM yyyy')}
              </div>
              <Button variant="outline" size="icon" onClick={nextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center p-8">
            <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No harvest data available</h3>
            <p className="text-muted-foreground text-center mb-4">Add harvests to see them in this calendar view</p>
            <Button asChild>
              <a href="/harvests/new">Add a Harvest</a>
            </Button>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-medium">Harvest Timeline</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center p-8">
            <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No timeline data</h3>
            <p className="text-muted-foreground text-center">Add harvests to see timeline data</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center space-x-4">
            <CardTitle className="text-lg font-medium">Calendar View</CardTitle>
            <Select
              value={selectedProduct}
              onValueChange={setSelectedProduct}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select product" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {Array.isArray(products) && products.map((product) => (
                  <SelectItem key={product} value={product || "unknown-product"}>
                    {product}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="icon" onClick={prevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="font-medium">
              {format(currentMonth, 'MMMM yyyy')}
            </div>
            <Button variant="outline" size="icon" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <CalendarLegend productGroups={filteredEvents.reduce((acc, event) => {
            if (!acc[event.product]) {
              acc[event.product] = {
                color: getProductColor(event.product),
                count: 0
              };
            }
            acc[event.product].count++;
            return acc;
          }, {} as Record<string, { color: string, count: number }>)} />
          
          <HarvestMonthCalendar 
            currentMonth={currentMonth}
            monthEvents={filteredEvents}
          />
          
          <UpcomingHarvestsList monthEvents={filteredEvents} />
        </CardContent>
      </Card>

      <HarvestProductTimeline events={filteredEvents} />
    </div>
  );
};

export default HarvestCalendarTimeline;
